import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface Presentation {
  slug: string;
  title: string;
  description: string;
  thumbnail?: string;
  date?: string;
}

// List of available presentations
const presentations: Presentation[] = [
  {
    slug: 'ai-costs-security',
    title: 'AI Costs and Security',
    description: 'Deployment approaches, security considerations, and cost analysis for AI models',
    date: 'February 2026',
  },
  {
    slug: 'outernet',
    title: 'Chat.outernet.nyc Case Study',
    description: 'Bringing NYC\'s literary history to life with AI-powered interfaces',
    date: 'November 2024',
  },
];

const Presentations = () => {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <Link to="/" className="text-xl font-bold text-gray-900 dark:text-white">
                Interface Research
              </Link>
              <span className="text-gray-500 dark:text-gray-400 ml-2">/ Presentations</span>
            </div>
            {user && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {user.email}
                </span>
                <button
                  onClick={logout}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Presentations
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Explore our case studies and project presentations
          </p>
        </div>

        {/* Presentations grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {presentations.map((presentation) => (
            <Link
              key={presentation.slug}
              to={`/presentations/${presentation.slug}`}
              className="group bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              {/* Thumbnail placeholder */}
              <div className="aspect-video bg-gradient-to-br from-glam-accent to-blue-600 flex items-center justify-center">
                <svg
                  className="w-16 h-16 text-white/50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                  />
                </svg>
              </div>

              <div className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-glam-accent transition-colors">
                  {presentation.title}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {presentation.description}
                </p>
                {presentation.date && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                    {presentation.date}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>

        {presentations.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No presentations available yet.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Presentations;
